import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Link2,
  Link2Off,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
} from "lucide-react";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5000";

const CARD = "bg-white/[0.04] border border-white/[0.07] rounded-2xl backdrop-blur-sm";
const BTN_PRIMARY =
  "inline-flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white rounded-lg px-3.5 py-2 text-sm font-medium transition-colors";
const BTN_SECONDARY =
  "inline-flex items-center gap-1.5 bg-white/[0.06] hover:bg-white/[0.09] border border-white/[0.1] text-white/70 hover:text-white rounded-lg px-3.5 py-2 text-sm font-medium transition-colors";

interface Connection {
  platform: string;
  platformUsername: string;
  platformAvatarUrl?: string;
  connectedAt: string;
  scope?: string;
}

const PLATFORMS: {
  id: string;
  label: string;
  color: string;
  description: string;
}[] = [
  {
    id: "twitter",
    label: "X / Twitter",
    color: "#000000",
    description: "Post tweets, threads, and updates",
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    color: "#0A66C2",
    description: "Share professional content and articles",
  },
  {
    id: "facebook",
    label: "Facebook / Instagram",
    color: "#1877F2",
    description: "Post to Facebook Pages and Instagram",
  },
  {
    id: "reddit",
    label: "Reddit",
    color: "#FF4500",
    description: "Share posts in relevant subreddits",
  },
  {
    id: "youtube",
    label: "YouTube",
    color: "#FF0000",
    description: "Upload YouTube Shorts and videos",
  },
  {
    id: "tiktok",
    label: "TikTok",
    color: "#010101",
    description: "Publish short-form video content",
  },
];

export default function ConnectedAccounts() {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [connectingPlatform, setConnectingPlatform] = useState<string | null>(
    null
  );
  const [disconnectingPlatform, setDisconnectingPlatform] = useState<
    string | null
  >(null);
  const [notice, setNotice] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const token = localStorage.getItem("adminToken");

  const fetchConnections = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/oauth/connections`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setConnections(Array.isArray(data) ? data : []);
    } catch {
      setConnections([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle OAuth redirect result
  useEffect(() => {
    const connected = searchParams.get("connected");
    const oauthError = searchParams.get("oauth_error");
    const platform = searchParams.get("platform");

    if (connected) {
      setNotice({
        type: "success",
        message: `${connected.charAt(0).toUpperCase() + connected.slice(1)} connected successfully!`,
      });
      setSearchParams({}, { replace: true });
    } else if (oauthError) {
      setNotice({
        type: "error",
        message: `Connection failed${platform ? ` for ${platform}` : ""}: ${decodeURIComponent(oauthError)}`,
      });
      setSearchParams({}, { replace: true });
    }

    fetchConnections();
  }, []);

  const handleConnect = async (platformId: string) => {
    if (!token) return;
    setConnectingPlatform(platformId);
    try {
      const res = await fetch(`${API_URL}/api/oauth/${platformId}/connect`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.error) {
        setNotice({ type: "error", message: data.error });
        setConnectingPlatform(null);
        return;
      }
      // Navigate to platform OAuth page
      window.location.href = data.redirectUrl;
    } catch {
      setNotice({ type: "error", message: "Failed to initiate OAuth flow" });
      setConnectingPlatform(null);
    }
  };

  const handleDisconnect = async (platformId: string) => {
    if (!token) return;
    setDisconnectingPlatform(platformId);
    try {
      await fetch(`${API_URL}/api/oauth/${platformId}/disconnect`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setConnections((prev) => prev.filter((c) => c.platform !== platformId));
      setNotice({ type: "success", message: `${platformId} disconnected` });
    } catch {
      setNotice({ type: "error", message: "Failed to disconnect" });
    } finally {
      setDisconnectingPlatform(null);
    }
  };

  const getConnection = (platformId: string) =>
    connections.find((c) => c.platform === platformId);

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Connected Accounts</h1>
          <p className="text-white/40 text-sm mt-1">
            Connect marketing social accounts for automated posting
          </p>
        </div>
        <button
          onClick={fetchConnections}
          className={BTN_SECONDARY}
          disabled={loading}
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Notice banner */}
      {notice && (
        <div
          className={`flex items-center gap-3 p-4 rounded-xl border text-sm font-medium ${
            notice.type === "success"
              ? "bg-green/10 border-green/20 text-green"
              : "bg-red/10 border-red/20 text-red"
          }`}
        >
          {notice.type === "success" ? (
            <CheckCircle2 size={16} />
          ) : (
            <XCircle size={16} />
          )}
          <span>{notice.message}</span>
          <button
            className="ml-auto text-white/30 hover:text-white/70"
            onClick={() => setNotice(null)}
          >
            ✕
          </button>
        </div>
      )}

      {/* Status summary */}
      <div className={`${CARD} p-4 flex flex-wrap gap-4 items-center`}>
        <div className="text-center px-4">
          <p className="text-2xl font-bold text-white">{connections.length}</p>
          <p className="text-white/40 text-xs mt-0.5">Connected</p>
        </div>
        <div className="w-px h-8 bg-white/[0.08]" />
        <div className="text-center px-4">
          <p className="text-2xl font-bold text-white">
            {PLATFORMS.length - connections.length}
          </p>
          <p className="text-white/40 text-xs mt-0.5">Not connected</p>
        </div>
        <div className="w-px h-8 bg-white/[0.08]" />
        <p className="text-white/40 text-sm flex-1">
          Connected accounts are used by the auto-posting daemon for scheduled
          marketing content.
        </p>
      </div>

      {/* Platform cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {PLATFORMS.map((platform) => {
          const conn = getConnection(platform.id);
          const isConnecting = connectingPlatform === platform.id;
          const isDisconnecting = disconnectingPlatform === platform.id;

          return (
            <div
              key={platform.id}
              className={`${CARD} p-5 flex items-center gap-4 transition-all duration-200 ${
                conn ? "border-white/[0.12]" : ""
              }`}
            >
              {/* Platform color dot */}
              <div
                className="w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center text-white font-bold text-sm"
                style={{ backgroundColor: platform.color }}
              >
                {platform.label.charAt(0)}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-white text-sm font-semibold">
                    {platform.label}
                  </p>
                  {conn && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase bg-green/15 text-green border border-green/25 rounded-full px-2 py-0.5">
                      <CheckCircle2 size={10} />
                      Live
                    </span>
                  )}
                </div>
                {conn ? (
                  <p className="text-white/50 text-xs mt-0.5 truncate">
                    @{conn.platformUsername} · Connected{" "}
                    {new Date(conn.connectedAt).toLocaleDateString()}
                  </p>
                ) : (
                  <p className="text-white/30 text-xs mt-0.5">
                    {platform.description}
                  </p>
                )}
              </div>

              {/* Action */}
              {conn ? (
                <button
                  onClick={() => handleDisconnect(platform.id)}
                  disabled={isDisconnecting}
                  className={`${BTN_SECONDARY} flex-shrink-0 text-red/70 hover:text-red border-red/20 hover:bg-red/10`}
                >
                  {isDisconnecting ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Link2Off size={14} />
                  )}
                  Disconnect
                </button>
              ) : (
                <button
                  onClick={() => handleConnect(platform.id)}
                  disabled={isConnecting || !!connectingPlatform}
                  className={`${BTN_PRIMARY} flex-shrink-0`}
                >
                  {isConnecting ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Link2 size={14} />
                  )}
                  Connect
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Setup instructions */}
      <div className={`${CARD} p-5`}>
        <h3 className="text-white/70 text-sm font-semibold mb-3 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-white/[0.08] flex items-center justify-center text-[10px]">
            i
          </span>
          Required environment variables
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-mono text-white/40">
          <span>TWITTER_OAUTH2_CLIENT_ID, TWITTER_OAUTH2_CLIENT_SECRET</span>
          <span>LINKEDIN_CLIENT_ID, LINKEDIN_CLIENT_SECRET</span>
          <span>FACEBOOK_APP_ID, FACEBOOK_APP_SECRET</span>
          <span>REDDIT_CLIENT_ID, REDDIT_CLIENT_SECRET</span>
          <span>GOOGLE_CLIENT_ID, GOOGLE_CLIENT_SECRET</span>
          <span>TIKTOK_CLIENT_KEY, TIKTOK_CLIENT_SECRET</span>
        </div>
        <p className="text-white/25 text-xs mt-3">
          Callback URL for each platform:{" "}
          <span className="text-white/40">{API_URL}/api/oauth/&#123;platform&#125;/callback</span>
        </p>
      </div>
    </div>
  );
}
